import { HTMLAttributes, useState } from "react"
import {
    Autocomplete,
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation, faInfoCircle, faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons"
import { isPositive, multiply } from "dinero.js"
import { NumericFormat } from "react-number-format"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../../currency/useMoney"
import {
    toCurrency,
    toDinero,
    toDineroLossy,
    toMonetaryAmount,
    toNumberLossy,
    zeroDinero,
} from "../../../../../../currency/currencyLib"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useNumber } from "../../../../../../localization/useNumber"
import {
    CUSTOM_PRODUCT_OPTION_ID,
    ProductionEditLine,
    ProductionProductOption,
    SetLineProductInput,
    SetLineWishInput,
} from "../useProductionOrderEdit"
import { useProductionLineStock } from "../useProductionLineStock"
import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"
import { MAX_PRICE_VALUE } from "../../../../../../../constants/inputValidationDefaults"
import { ProductionClampedName } from "./ProductionClampedName"

const useStyles = makeStyles()((theme) => ({
    rowView: {
        padding: "1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    rowEdit: {
        padding: "1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
    },
    leftGroup: {
        flexGrow: 1,
        minWidth: 0,
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
            rowGap: ".5rem",
        },
    },
    rightGroup: {
        display: "grid",
        gridTemplateColumns: "4rem 6.5rem 2.5rem",
        alignItems: "center",
        columnGap: ".75rem",
        width: "14.5rem",
        flexShrink: 0,
    },
    countView: {
        flexShrink: 0,
        width: "2.875rem",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[800],
    },
    nameView: {
        fontWeight: 400,
        color: theme.palette.tailwind.slate[800],
    },
    counter: {
        flexShrink: 0,
        width: "fit-content",
        height: "2.5rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".25rem",
        overflow: "hidden",
    },
    counterButton: {
        flexShrink: 0,
        width: "2.5rem",
        cursor: "pointer",
        background: theme.palette.tailwind.slate[200],
        color: theme.palette.tailwind.slate[800],
        fontSize: "1rem",
    },
    counterButtonDisabled: {
        cursor: "default",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[400],
    },
    countBox: {
        flexShrink: 0,
        width: "3rem",
        background: theme.palette.background.paper,
        "& .MuiOutlinedInput-root": {
            height: "2.5rem",
            borderRadius: 0,
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "& .MuiOutlinedInput-input": {
            padding: ".5rem",
            textAlign: "center",
            fontSize: "1rem",
            color: theme.palette.tailwind.slate[800],
        },
    },
    productField: {
        position: "relative",
        flexGrow: 1,
        minWidth: "8rem",
        background: theme.palette.background.paper,
        "& .MuiOutlinedInput-root": {
            minHeight: "2.5rem",
            borderRadius: ".25rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
        "& .MuiAutocomplete-clearIndicator": {
            visibility: "visible",
        },
        "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
            color: theme.palette.tailwind.slate[500],
        },
        "& .MuiFormHelperText-root": {
            position: "absolute",
            top: "100%",
            left: 0,
            margin: ".125rem 0 0",
        },
    },
    badge: {
        flexShrink: 0,
        textTransform: "uppercase",
    },
    legacyIcon: {
        color: theme.palette.tailwind.slate[400],
    },
    inactiveIcon: {
        color: theme.palette.tailwind.red[600],
    },
    wishNameField: {
        flexGrow: 1,
        minWidth: "8rem",
        background: theme.palette.background.paper,
    },
    // Typed in place of the read-only unit price, so it has to fit that column without moving it.
    wishPriceField: {
        width: "100%",
        background: theme.palette.background.paper,
        "& .MuiOutlinedInput-root": {
            height: "2.5rem",
            borderRadius: ".25rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
        "& .MuiOutlinedInput-input": {
            padding: ".5rem",
            textAlign: "right",
        },
    },
    optionName: {
        color: theme.palette.tailwind.slate[900],
    },
    optionPrice: {
        color: theme.palette.tailwind.slate[500],
    },
    optionStock: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[500],
    },
    optionSoldOut: {
        color: theme.palette.tailwind.red[600],
    },
    unitPrice: {
        flexShrink: 0,
        textAlign: "right",
        fontWeight: 500,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[500],
    },
    lineTotal: {
        textAlign: "right",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
    removeButton: {
        justifySelf: "center",
        width: "2rem",
        height: "2rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
        color: theme.palette.tailwind.slate[500],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            borderColor: theme.palette.tailwind.red[200],
            backgroundColor: theme.palette.tailwind.red[50],
            color: theme.palette.tailwind.red[600],
        },
    },
}))

type Props = Readonly<{
    line: ProductionEditLine
    isEditing: boolean
    productOptions: ReadonlyArray<ProductionProductOption>
    isProductsLoading: boolean
    isEditLoading: boolean
    isTouched: boolean
    usedProductIds: ReadonlyArray<number>
    onCountChange: (key: string, value: number) => void
    onProductChange: (key: string, product: SetLineProductInput) => void
    onWishChange: (key: string, wish: SetLineWishInput) => void
    onRemove: (key: string) => void
}>

export function ProductionOrderLineRow({
    line,
    isEditing,
    productOptions,
    isProductsLoading,
    isEditLoading,
    isTouched,
    usedProductIds,
    onCountChange,
    onProductChange,
    onWishChange,
    onRemove,
}: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const numberFactory = useNumber({})

    const {
        disableIncrease,
        isInactiveProduct,
        countInputValue,
        handleCountInput,
        handleCountBlur,
        handleIncrease,
        isOptionSoldOut,
        getOptionStock,
        handleProductSelect,
    } = useProductionLineStock({ line, productOptions, usedProductIds, onCountChange, onProductChange })

    // A product wish is typed in place, so its fields keep their own text state (the line only stores the
    // parsed values). An unpriced wish starts with an empty price field rather than a formatted zero.
    const [wishName, setWishName] = useState(line.name)
    const [wishPrice, setWishPrice] = useState<number | undefined>(() =>
        isPositive(toDinero(line.itemPrice)) ? toNumberLossy(toDinero(line.itemPrice)) : undefined,
    )

    const emitWish = (name: string, priceValue: number | undefined) => {
        const price =
            priceValue !== undefined && priceValue > 0
                ? toMonetaryAmount(toDineroLossy(priceValue, toCurrency(line.itemPrice.currency)))
                : toMonetaryAmount(zeroDinero(toCurrency(line.itemPrice.currency)))
        onWishChange(line.key, { name: name.trim(), price })
    }

    if (!isEditing) {
        return (
            <Stack direction="row" alignItems="center" spacing=".75rem" className={classes.rowView}>
                <Typography variant="body1" component="span" className={classes.countView}>
                    {line.count}
                </Typography>
                <ProductionClampedName
                    name={line.name}
                    dialogTitleId="meeting-service-production-view-product-name-dialog-title"
                    titleClassName={classes.nameView}
                />
            </Stack>
        )
    }

    const unitPriceLabel = moneyFactory.formatAsNumber(toDinero(line.itemPrice))
    const lineTotalLabel = moneyFactory.formatAsNumber(multiply(toDinero(line.itemPrice), line.count))

    // The line's current selection as an option. A product wish has a name + price but no productId, so it uses
    // a display-only sentinel id (never sent to the backend); a blank line (no name yet) shows the placeholder.
    const currentValue: ProductionProductOption | null = line.name
        ? { productId: line.productId ?? CUSTOM_PRODUCT_OPTION_ID, name: line.name, itemPrice: line.itemPrice }
        : null
    // Products already used by other lines stay in the list; picking one is rejected with a snackbar instead.
    const options =
        currentValue && !productOptions.some((option) => option.productId === currentValue.productId)
            ? [currentValue, ...productOptions]
            : productOptions

    // Disabled while loading (until we know editability). An extra-service line can't be modified in place
    // (its type/count are fixed — Task 263), but it CAN be removed and re-added via the add-line dropdown.
    const isLegacyExtraService = line.legacyOrderLineId !== undefined
    const isDisabled = isEditLoading || isProductsLoading || line.isExtraService
    const isRemoveDisabled = isEditLoading || isProductsLoading

    // A blank line (no product picked, or a wish with no name) is invalid; show it only once touched, like
    // FormikAutocomplete.
    const showProductRequired =
        isTouched && !line.isExtraService && line.productId === undefined && line.name.trim() === ""
    // A wish is only complete once it is priced, so flag an empty/zero price the same way.
    const showWishPriceRequired = isTouched && line.isProductWish && !isPositive(toDinero(line.itemPrice))

    const renderProductOption = (optionProps: HTMLAttributes<HTMLLIElement>, option: ProductionProductOption) => {
        const { soldOut, label } = getOptionStock(option)
        return (
            <Box component="li" {...optionProps} key={option.productId}>
                <Stack>
                    <Typography variant="subtitle1" component="span" className={classes.optionName}>
                        {option.name}
                    </Typography>
                    <Typography variant="body2" component="span" className={classes.optionPrice}>
                        {moneyFactory.formatAsNumber(toDinero(option.itemPrice))}
                    </Typography>
                    {label && (
                        <Typography
                            variant="caption"
                            component="span"
                            className={cx(classes.optionStock, soldOut && classes.optionSoldOut)}
                        >
                            {label}
                        </Typography>
                    )}
                </Stack>
            </Box>
        )
    }

    return (
        <Stack direction="row" alignItems="center" spacing=".75rem" className={classes.rowEdit}>
            <Stack direction="row" alignItems="center" spacing=".75rem" className={classes.leftGroup}>
                <Stack direction="row" className={classes.counter}>
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        className={cx(classes.counterButton, isDisabled && classes.counterButtonDisabled)}
                        onClick={isDisabled ? undefined : () => onCountChange(line.key, Math.max(line.count - 1, 0))}
                    >
                        <FontAwesomeIcon icon={faMinus} />
                    </Stack>
                    <TextField
                        className={classes.countBox}
                        size="small"
                        value={countInputValue}
                        onChange={handleCountInput}
                        onBlur={handleCountBlur}
                        inputProps={{ maxLength: 4 }}
                        disabled={isDisabled}
                    />
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        className={cx(
                            classes.counterButton,
                            (isDisabled || disableIncrease) && classes.counterButtonDisabled,
                        )}
                        onClick={isDisabled ? undefined : handleIncrease}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Stack>
                </Stack>

                {line.isExtraService && (
                    <LocalizedStrict id="meeting-service-production-view-service-badge" attrs={{ label: true }}>
                        <StandardChip variant="info" size="small" className={classes.badge} label="Service" />
                    </LocalizedStrict>
                )}
                {isLegacyExtraService && (
                    <LocalizedStrict
                        id="meeting-service-production-view-legacy-service-tooltip"
                        attrs={{ title: true }}
                    >
                        <Tooltip
                            placement="top"
                            arrow
                            title="This is a legacy extra service from the old portal. It cannot be changed, but it can be removed."
                        >
                            <span tabIndex={0}>
                                <FontAwesomeIcon icon={faCircleExclamation} className={classes.legacyIcon} size="lg" />
                            </span>
                        </Tooltip>
                    </LocalizedStrict>
                )}
                {isInactiveProduct && !isProductsLoading && (
                    <LocalizedStrict
                        id="create-order-order-summary-meeting-services-product-warning-missing-product-tooltip"
                        attrs={{ title: true }}
                    >
                        <Tooltip
                            placement="bottom"
                            arrow
                            title="This product is not available in the selected shop list"
                        >
                            <FontAwesomeIcon icon={faInfoCircle} className={classes.inactiveIcon} size="lg" />
                        </Tooltip>
                    </LocalizedStrict>
                )}
                {line.isProductWish ? (
                    <>
                        <LocalizedStrict
                            id="meeting-service-production-view-wish-name-field"
                            attrs={{ placeholder: true }}
                        >
                            <TextField
                                className={classes.wishNameField}
                                size="small"
                                placeholder=""
                                value={wishName}
                                disabled={isDisabled}
                                error={showProductRequired}
                                helperText={
                                    showProductRequired
                                        ? l10n.getString("meeting-service-production-view-product-required")
                                        : undefined
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <LocalizedStrict
                                                id="meeting-service-production-view-wish-badge"
                                                attrs={{ label: true }}
                                            >
                                                <StandardChip
                                                    variant="yellow"
                                                    size="small"
                                                    className={classes.badge}
                                                    label="Wish"
                                                />
                                            </LocalizedStrict>
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(event) => {
                                    setWishName(event.target.value)
                                    emitWish(event.target.value, wishPrice)
                                }}
                            />
                        </LocalizedStrict>
                    </>
                ) : (
                    <Autocomplete<ProductionProductOption>
                        className={classes.productField}
                        options={options}
                        value={currentValue}
                        disabled={isDisabled}
                        loading={isProductsLoading}
                        onChange={(_event, value) => handleProductSelect(value)}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.productId === value.productId}
                        getOptionDisabled={isOptionSoldOut}
                        renderOption={renderProductOption}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder={l10n.getString(
                                    "meeting-service-production-view-product-search-placeholder",
                                )}
                                error={showProductRequired}
                                helperText={
                                    showProductRequired
                                        ? l10n.getString("meeting-service-production-view-product-required")
                                        : undefined
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isProductsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            </Stack>

            <Box className={classes.rightGroup}>
                {line.isProductWish ? (
                    <LocalizedStrict
                        id="meeting-service-production-view-wish-price-field"
                        attrs={{ placeholder: true }}
                    >
                        <NumericFormat
                            customInput={TextField}
                            className={classes.wishPriceField}
                            size="small"
                            placeholder=""
                            value={wishPrice ?? ""}
                            disabled={isDisabled}
                            error={showWishPriceRequired}
                            thousandSeparator={numberFactory.groupSeparator}
                            decimalSeparator={numberFactory.decimalSeparator}
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= MAX_PRICE_VALUE}
                            onValueChange={(values, sourceInfo) => {
                                if (sourceInfo.source !== "event") return
                                setWishPrice(values.floatValue)
                                emitWish(wishName, values.floatValue)
                            }}
                        />
                    </LocalizedStrict>
                ) : (
                    <Typography variant="body2" className={classes.unitPrice}>
                        {unitPriceLabel}
                    </Typography>
                )}

                <Typography variant="body1" className={classes.lineTotal}>
                    {lineTotalLabel}
                </Typography>

                <LocalizedStrict id="meeting-service-production-view-remove-line-button" attrs={{ "aria-label": true }}>
                    <IconButton
                        size="small"
                        className={classes.removeButton}
                        aria-label="Remove"
                        disabled={isRemoveDisabled}
                        onClick={() => onRemove(line.key)}
                    >
                        <FontAwesomeIcon icon={faTrashCan} />
                    </IconButton>
                </LocalizedStrict>
            </Box>
        </Stack>
    )
}
